import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { logoutAsync } from '../../features/auth/authSlice';
import NotificationBell from '../organisms/NotificationBell';

const Navbar = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const { t, i18n } = useTranslation();

  const todayLabel = (() => {
    const raw = new Date().toLocaleDateString(
      i18n.language === 'ar' ? 'ar-MA' : 'fr-FR',
      { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
    );
    return raw.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  })();

  const handleLogout = () => {
    dispatch(logoutAsync());
    navigate('/');
  };

  return (
    <header
      className="bg-white px-4 md:px-6 flex items-center justify-between flex-shrink-0 z-20"
      style={{
        height: '64px',
        borderBottom: '1px solid #E2E8F0',
        boxShadow: '0 1px 4px rgba(15, 23, 42, 0.06)',
      }}
    >
      {/* ── Gauche ── */}
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="md:hidden w-9 h-9 rounded-xl flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <div className="flex flex-col justify-center min-w-0">
          <p
            className="truncate"
            style={{ fontSize: '13px', color: '#6B7280', fontWeight: 500, lineHeight: 1.3 }}
          >
            {`Bonjour, ${user?.username || ''} 👋`}
          </p>
          <h1
            className="truncate"
            style={{ fontSize: '18px', color: '#0F172A', fontWeight: 700, lineHeight: 1.25 }}
          >
            {t('home.welcome')}
          </h1>
        </div>
      </div>

      {/* ── Droite (gauche → droite : date · divider · cloche · divider · avatar) ── */}
      <div className="flex items-center flex-shrink-0 gap-3">

        {/* 1. Cloche (inchangée) */}
        <NotificationBell />

        {/* 2. Divider */}
        <div
          className="hidden sm:block flex-shrink-0"
          style={{ width: '1px', height: '28px', background: '#E2E8F0' }}
        />

        {/* 3. Bloc date */}
        <div
          className="hidden sm:flex flex-col items-end justify-center"
          style={{
            background: '#F8FAFC',
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            padding: '6px 12px',
          }}
        >
          <p style={{ fontSize: '13px', color: '#0F172A', fontWeight: 600, lineHeight: 1.3 }}>
            {todayLabel}
          </p>
          <p style={{ fontSize: '11px', color: '#9CA3AF', lineHeight: 1.2 }}>
            {t('home.todayLabel')}
          </p>
        </div>

        {/* 4. Divider */}
        <div
          className="hidden sm:block flex-shrink-0"
          style={{ width: '1px', height: '28px', background: '#E2E8F0' }}
        />

        {/* Mobile : icône déconnexion séparée */}
        <button
          onClick={handleLogout}
          title={t('common.logout')}
          className="sm:hidden w-8 h-8 rounded-xl flex items-center justify-center text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        </button>

        {/* Mobile : avatar sans action */}
        <div className="sm:hidden w-8 h-8 rounded-full bg-[#1F6B57] flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-bold text-white uppercase">{user?.username?.[0] || 'A'}</span>
        </div>

        {/* Desktop : bloc complet avec déconnexion */}
        <button
          onClick={handleLogout}
          title={t('common.logout')}
          className="hidden sm:flex items-center gap-2 px-2 md:px-3 py-1.5 rounded-xl hover:bg-gray-50 transition-colors group"
        >
          <div className="w-8 h-8 rounded-full bg-[#1F6B57] flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-white uppercase">{user?.username?.[0] || 'A'}</span>
          </div>
          <div className="text-left">
            <p className="text-sm font-medium text-gray-700 leading-tight">{user?.username || 'Admin'}</p>
            <p className="text-xs text-gray-400 leading-tight">
              {t(`roles.${user?.role}`, { defaultValue: user?.role || '' })}
            </p>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-gray-400 group-hover:text-red-500 transition-colors"
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        </button>
      </div>
    </header>
  );
};

export default Navbar;





